import React from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store/store';
import {
  loginUser,
  logoutUser,
  checkAuthStatus,
  clearError,
  clearSuccess,
  resetAuth,
} from './authSlice';
import { getRuntimeConfig } from '../../config/runtimeConfig';
import SessionTimeoutWarning from '../../shared/components/SessionTimeoutWarning';
import { authStorage } from './authStorage';

export interface AuthContextType {
  initialized: boolean;
  authenticated: boolean;
  user: any;
  error: string | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
  clearSuccess: () => void;
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const {
    isAuthenticated,
    userInfo,
    error,
    loading,
    success,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  } = useSelector((state: RootState) => state.auth);
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    // In development mode, optionally clear auth data if CLEAR_AUTH_ON_START is set
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_CLEAR_AUTH_ON_START === 'true'
    ) {
      authStorage.clearAuthData();
    }

    // Rely on centralized auth storage to verify cached authentication state
    const authData = authStorage.getAuthData();

    if (authData && !isAuthenticated) {
      dispatch(checkAuthStatus()).finally(() => setInitialized(true));
      return;
    }
    setInitialized(true);
  }, []);

  const login = React.useCallback(async () => {
    if (isAuthenticated) return;

    // Redirect to the native login page instead of Keycloak
    window.location.href = '/login';
  }, [isAuthenticated]);

  const logout = React.useCallback(async () => {
    try {
      console.log('🔄 AuthProvider - processing secure logout route');
      authStorage.clearAuthData();
      await dispatch(logoutUser()).unwrap();
      dispatch(resetAuth());
      window.location.href = '/logout-confirmation';
    } catch (error) {
      authStorage.clearAuthData();
      window.location.href = '/logout-confirmation';
    }
  }, [dispatch]);

  const refresh = React.useCallback(async () => {
    try {
      await dispatch(checkAuthStatus()).unwrap();
    } catch (error) {
      console.error('Refresh Failed', error);
      throw error;
    }
  }, [dispatch]);

  const handleClearError = React.useCallback(() => dispatch(clearError()), [dispatch]);
  const handleClearSuccess = React.useCallback(() => dispatch(clearSuccess()), [dispatch]);

  const value = React.useMemo<AuthContextType>(
    () => ({
      initialized,
      authenticated: isAuthenticated,
      user: userInfo,
      error,
      loading,
      login,
      logout,
      refresh,
      clearError: handleClearError,
      clearSuccess: handleClearSuccess,
    }),
    [
      initialized,
      isAuthenticated,
      userInfo,
      error,
      loading,
      login,
      logout,
      refresh,
      handleClearError,
      handleClearSuccess,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutWarning
        authenticated={isAuthenticated}
        accessTokenExpiresAt={accessTokenExpiresAt}
        refreshTokenExpiresAt={refreshTokenExpiresAt}
        onStayLoggedIn={refresh}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};
